import hoistNonReactStatics from 'hoist-non-react-statics';
import reactUpdate, {Spec} from 'immutability-helper';
import React from 'react';
import isEqual from 'react-fast-compare';
import {Unsubscribe} from 'redux';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import documentStore from 'notebook/documents/DocumentStore';
import {Selector} from 'notebook/ELNConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {MetadataForRestore} from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {TableLocationNameArgs, FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {SpreadsheetStoreState} from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {TableOpContextProvider} from 'notebook/tables/spreadsheets/TableOpContext';
import {STRING_RESOLUTION_INITIAL_STATE} from 'notebook/tables/string-resolution/reduceStringResolution';
import {
  Cells,
  CrossTableCellColors,
  Table,
  TableIndex,
  TableOperation,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import {MODELS_INITIAL_STATE} from 'redux-stores/reduceModels';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';
import {WORKFLOWS_INITIAL_STATE} from 'workflows/reduceWorkflows';

import {NoteRuntimeSnapshot} from '../TableStoreTransforms';

/**
 * External contract for spreadsheet evaluation.
 *
 * TRoot is the container type from which items are retrieved and which is updated.
 * This is typically a Note, but might be a Protocol or a NoteLinkedGroup.
 *
 * TItem is the item that feeds a MediocreItem.
 * This is typically a NotePart, but could be a Protocol's Step or a NoteLinkedGroup's inner NotePart.
 */
export interface ExternalProps<TItem = unknown, TRoot = unknown> {
  extraUsedTableNames?: () => Array<string>;
  getAllStoreDependenciesFromSpreadsheetRoot?: (spreadsheetRoot: TRoot) => StoreDependencyMap;
  getContent?: (item: TItem) => unknown;
  getItems?: (root: TRoot) => Array<TItem>;
  getRoot?: () => TRoot;
  getSpreadsheetEvalCacheKey?: () => string;
  getSpreadsheetTableName?: (item: TItem) => string;
  getType?: (item: TItem) => string;
  getCid?: (item: TItem) => string;
  itemsAccessor?: (op: {[i: number]: Spec<TItem>}) => Spec<TRoot>;
  makeFullTableMap: (props: unknown) => FullTableMap;
  selectorsFromProps: (props: unknown) => Array<Selector>;
  setContent?: (type: string, content: object) => Spec<TItem>;
  spreadsheetRoot: (props: unknown) => TRoot;
  spreadsheetTypes?: Array<string>;
  updateRoot?: (
    root: TRoot,
    opts?: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    },
    updatedRelatedDocuments?: {[documentId: string]: Note}
  ) => void;
  shouldDisableFormulas?: () => boolean;
  requestSelection?: (selectionRestoreData: MetadataForRestore<TableIndex>) => void;
  tempTableStepUpdateHandler?: (fullTable: Table) => void;
  tempTableStepResetHandler?: (tableName: string) => void;
  // A null NoteRuntimeSnapshot can be specified if formulas are disabled.
  getNoteRuntimeSnapshot: (props: unknown) => NoteRuntimeSnapshot | null;

  // HACK: This prop only exists to work around spreadsheet evaluation gaps in the mixture ingredients table.
  // It should not be used in typical usage. See BNCH-20069.
  disallowCellRefs?: boolean;

  // Used by file specific store to re-evaluate on demand
  fileId?: string;
  /** If the SpreadsheetEvaluation is for within a NoteLinkedGroup, the following prop is set */
  group?: UnknownNoteLinkedGroup;
  tableNamespace?: string;
}

export interface ComponentProps<TItem = unknown, TRoot = unknown> extends ExternalProps<TItem, TRoot> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRef?: (element: any) => void;
}

export interface EnhancedProps extends State {
  evaluateTables: (
    fullTableMap: FullTableMap,
    selectors: Array<Selector>,
    noteRuntimeSnapshot: NoteRuntimeSnapshot | null
  ) => {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}};
  setMultiEditingForTable: (
    {tableName, documentName}: TableLocationNameArgs,
    {
      index,
      editing,
      text,
      typedValue,
      selection,
    }: {
      index: TableIndex | null;
      editing: boolean;
      text: string;
      typedValue: TypedValue;
      selection: {cid: string; start: number; end: number} | null;
    }
  ) => void;
  tryInsert: (
    {tableName, documentName}: TableLocationNameArgs,
    e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
  ) => boolean;
  canInsert: (e: {start: number; end: number; cid: string; shift: boolean}) => boolean;
  disallowMultiExpand: () => boolean;
  isTableNameUsed: (tableName: string) => boolean;
  updateSelectors: (newSelectors: Array<Selector>) => void;
  usedTableNames: () => Array<string>;
  performTableOp: (tableOperation: TableOperation) => void;
}

export interface State extends Pick<SpreadsheetStoreState, 'editingTable' | 'editingDocumentName'> {
  cellColors: CrossTableCellColors;
  fullTableMap: FullTableMap;
  tempFullTableMap: FullTableMap;
  computedTables: OutputTableMap;
  showExcessLabels: boolean;
  tableNamespace: string;
  selectorCounts: {[selectorId: number]: number};
}

export default function withSpreadsheetEvaluation<P extends ComponentProps<T, R>, T = unknown, R = unknown>(
  WrappedComponent: React.ComponentClass<P & EnhancedProps>
): React.ComponentClass<P> {
  class Component extends React.Component<P, State> {
    _storeDependencies: StoreDependencyMap;

    _storeState: SpreadsheetEvalUtil.SpreadsheetEvalStoreState;

    _reduxUnsubscribe: Unsubscribe | null;

    constructor(props: P) {
      super(props);
      this._storeState = {
        elnResolution: STRING_RESOLUTION_INITIAL_STATE.resolutionResultsByResolutionQuery,
        workflows: WORKFLOWS_INITIAL_STATE,
        fileSummariesById: MODELS_INITIAL_STATE.fileSummariesById,
        storablesById: {},
        storableSummariesById: {},
      };
      this._storeDependencies = {};
      this._reduxUnsubscribe = null;
      const tableNamespace = props.tableNamespace ?? _.uniqueId('table-ns');
      // eslint-disable-next-line no-restricted-syntax
      this.state = _.extend({}, this._spreadsheetStoreState(tableNamespace), {
        tableNamespace,
      });
    }

    _getNoteRuntimeSnapshotIfEnabled(this: void, props: P): NoteRuntimeSnapshot | null {
      if (!BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER) {
        return null;
      }
      return props.getNoteRuntimeSnapshot(props);
    }

    override UNSAFE_componentWillMount(): void {
      const fullTableMap = this.props.makeFullTableMap(this.props);
      const selectors = this.props.selectorsFromProps(this.props);
      const noteRuntimeSnapshot = this._getNoteRuntimeSnapshotIfEnabled(this.props);
      this.setState({tempFullTableMap: fullTableMap});
      this._updateEvaluatedTableState(fullTableMap, selectors, noteRuntimeSnapshot);
      this.setState({fullTableMap});
    }

    override componentDidMount(): void {
      this._storeDependencies = this.getAllStoreDependencies(this.props);
      // Force an update whenever anything in the ELN redux tree updates (resolution results or
      // resolution loading state), when the stage runs for the entry are changed,
      // or when the bioentities with ids in this._storeDependencies update in the redux store.
      this._reduxUnsubscribe = getStore().subscribe(this._updateIfStoreDependenciesChanged);
      // Listen to storable IDs needed by transfer table custom eval.
      // The storable IDs are computed by getStoreDependenciesForTransferTable
      StorableStore.addChangeListener(this._updateIfStoreDependenciesChanged);
      SpreadsheetStore.addChangeListener(this._spreadsheetStoreChangeHandler);
    }

    override componentWillUnmount(): void {
      if (this._reduxUnsubscribe) {
        this._reduxUnsubscribe();
      }
      StorableStore.removeChangeListener(this._updateIfStoreDependenciesChanged);
      SpreadsheetStore.removeChangeListener(this._spreadsheetStoreChangeHandler);
    }

    override UNSAFE_componentWillReceiveProps(nextProps: P): void {
      // Determine if we should trigger table evaluation based on new props.

      const {noteRuntimeSnapshot, selectors, fullTableMap, shortCircuit} =
        getDataForUpdatingEvaluatedTableState(
          this.props as ComponentProps<unknown>,
          this.state,
          nextProps,
          this._getNoteRuntimeSnapshotIfEnabled
        );
      if (shortCircuit) {
        return;
      }

      // Run table evaluation and update component state. If persistEval is enabled,
      // "evaluation" will just mean grabbing the new evaluationSnapshot from the NRS
      this._storeDependencies = this.getAllStoreDependencies(nextProps);
      this.setState({tempFullTableMap: fullTableMap!});
      this._updateEvaluatedTableState(fullTableMap!, selectors, noteRuntimeSnapshot);
      this.setState({fullTableMap: fullTableMap!});
    }

    override componentDidUpdate(prevProps: P): void {
      if (this.props.fileId !== prevProps.fileId) {
        this._spreadsheetStoreChangeHandler();
      }
    }

    _handleChangeTempFullTableMap = (newTempFullTableMap: FullTableMap): void => {
      this.setState({tempFullTableMap: newTempFullTableMap});
    };

    _updateIfStoreDependenciesChanged = (): void => {
      // If an evaluation snapshot exists then the entry has been sent for review and we do not want to rerun
      // evaluation which could potentially cause the entry to change
      if (this._getNoteRuntimeSnapshotIfEnabled(this.props)?.evaluationSnapshot != null) {
        return;
      }

      this._storeState = SpreadsheetEvalUtil.updateIfStoreDependenciesChanged({
        storeState: this._storeState,
        storeDependencies: this._storeDependencies,
        tempFullTableMap: this.state.tempFullTableMap,
        handleChangeTempFullTableMap: this._handleChangeTempFullTableMap,
        makeFullTableMap: () => this.props.makeFullTableMap(this.props),
        selectorsFromProps: () => this.props.selectorsFromProps(this.props),
        updateEvaluatedTableState: this._updateEvaluatedTableState,
        fileId: this.props.fileId ?? null,
        isNoteLinkedGroup: Boolean(this.props.group),
      });
    };

    _hasFormulasDisabled(): boolean {
      return this.props.shouldDisableFormulas == null ? false : this.props.shouldDisableFormulas();
    }

    evaluateTables = (
      fullTableMap: FullTableMap,
      selectors: Array<Selector>,
      noteRuntimeSnapshot: NoteRuntimeSnapshot | null
    ): {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}} => {
      if (
        BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_RENDER &&
        noteRuntimeSnapshot &&
        noteRuntimeSnapshot.evaluationSnapshot
      ) {
        if (window.debug) {
          // eslint-disable-next-line no-console
          console.info('[EXCESS] Rendering evaluation from snapshot', noteRuntimeSnapshot);
        }
        // While we maintain both persisted and non-persisted evaluation, we copy the data out of the
        // NoteRuntimeSnapshot into component state.
        return {
          computedTables: noteRuntimeSnapshot.evaluationSnapshot,
          selectorCounts: SpreadsheetEvalUtil.getSelectorCounts(fullTableMap),
        };
      } else {
        return SpreadsheetEvalUtil.evaluateTables({
          fullTableMap,
          selectors,
          oldComputedTables: this.state.computedTables,
          hasFormulasDisabled: this._hasFormulasDisabled(),
          spreadsheetEvalCacheKey: this.props.getSpreadsheetEvalCacheKey
            ? this.props.getSpreadsheetEvalCacheKey()
            : null,
          disallowCellRefs: this.props.disallowCellRefs,
          fileId: this.props.fileId ?? null,
        });
      }
    };

    _updateEvaluatedTableState = (
      fullTableMap: FullTableMap,
      selectors: Array<Selector>,
      noteRuntimeSnapshot: NoteRuntimeSnapshot | null
    ): void => {
      const {computedTables, selectorCounts} = this.evaluateTables(
        fullTableMap,
        selectors,
        noteRuntimeSnapshot
      );
      this.setState({computedTables, selectorCounts});
    };

    _spreadsheetStoreState(
      tableNamespace: string
    ): Pick<State, 'cellColors' | 'editingTable' | 'editingDocumentName' | 'showExcessLabels'> {
      const documentName = this.props.fileId ? documentStore.getDocument(this.props.fileId)?.name : undefined;
      return SpreadsheetEvalUtil.getSpreadsheetStoreState(
        tableNamespace,
        this._hasFormulasDisabled(),
        undefined,
        documentName
      );
    }

    _spreadsheetStoreChangeHandler = (): void => {
      this.setState(this._spreadsheetStoreState(this.state.tableNamespace));
    };

    setMultiEditingForTable = (
      {tableName, documentName}: TableLocationNameArgs,
      {
        index,
        editing,
        text,
        typedValue,
        selection,
      }: {
        index: TableIndex;
        editing: boolean;
        text: string;
        typedValue: TypedValue;
        selection: {cid: string; start: number; end: number} | null;
      }
    ): void => {
      SpreadsheetEvalUtil.setMultiEditingForTable(
        {tableName, documentName},
        this.state.tableNamespace,
        this.props.fileId ?? null,
        {
          index,
          editing,
          text,
          typedValue,
          selection,
        }
      );
    };

    tryInsert = (
      {tableName, documentName}: TableLocationNameArgs,
      e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
    ): boolean =>
      SpreadsheetEvalUtil.tryInsert({
        tableName,
        documentName,
        e,
        hasFormulasDisabled: this._hasFormulasDisabled(),
        tableNamespace: this.state.tableNamespace,
      });

    canInsert = (e: {start: number; end: number; cid: string; shift: boolean}): boolean =>
      SpreadsheetEvalUtil.canInsert({
        e,
        hasFormulasDisabled: this._hasFormulasDisabled(),
        tableNamespace: this.state.tableNamespace,
        editingTable: this.state.editingTable,
        editingDocumentName: this.state.editingDocumentName,
      });

    /**
     * Performs an operation on a table, updating the appropriate react components
     * @param  {Number} index   The index of the targetItem containing the table with targetTableName
     * @param  {Function} op    (originalTableMap, targetTableName) -> {tables: newTableMap, box}, where
     *                            originalTableMap {Object}  Maps original names of tables to original tables.
     *                            targetTableName {String}   The name of the table being directly modified
     *                            newTableMap {Object}       Maps *original* names of tables to new tables.
     *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
     *                                                       is specified
     */
    _performTableOp = ({index, op, options}: TableOperation): void => {
      // Many of these props are not specified for protocol viewers, which do not perform
      // table operations. However, making this typecheck without failing silently is difficult
      // since assertions do not affect typechecking.
      SpreadsheetEvalUtil.performTableOp({
        index,
        fileId: this.props.fileId ?? null,
        op,
        options,
        spreadsheetTypes: this.props.spreadsheetTypes!,
        computedTables: this.state.computedTables,
        getItems: this.props.getItems!,
        getRoot: this.props.getRoot!,
        getType: this.props.getType!,
        getCid: this.props.getCid!,
        // Since this will only get called with tables, we can cast this to only return the known table type
        getContent: this.props.getContent as (item: T) => {table: Table},
        setContent: this.props.setContent!,
        requestSelection: this.props.requestSelection!,
        updateRoot: this.props.updateRoot!,
        itemsAccessor: this.props.itemsAccessor!,
      });
    };

    // We disable expanding a multiselect via drag while the a table is actively being edited,
    // as this triggers the child table to clear its selection (via multiMouseLeaveHandler), however,
    // the table is still identified by state.editingTable. We let the table keep its selection so that
    // it can handle finalizing the edit.
    disallowMultiExpand = (): boolean => this.state.editingTable != null;

    // Determines whether tableName is used anywhere within the existing note.
    isTableNameUsed = (tableName: string): boolean => this.usedTableNames().includes(tableName);

    getAllStoreDependencies(props: P): StoreDependencyMap {
      // Not all users of this HOC pass `getAllStoreDependenciesFromSpreadsheetRoot`.
      if (!this.props.getAllStoreDependenciesFromSpreadsheetRoot) {
        return {};
      }
      return this.props.getAllStoreDependenciesFromSpreadsheetRoot(this.props.spreadsheetRoot(props));
    }

    updateSelectors = (newSelectors: Array<Selector>): void => {
      // This must be an object type because reactUpdate infers the type to determine what operations are allowed.
      const root = reactUpdate(this.props.getRoot!() as object, {$merge: {selectors: newSelectors}}) as R;
      this.props.updateRoot!(root);
    };

    // Utility methods

    usedTableNames = (): Array<string> => {
      // eslint-disable-next-line no-restricted-syntax
      let usedTableNames: Array<string> = _.keys(this.state.fullTableMap);
      if (typeof this.props.extraUsedTableNames === 'function') {
        usedTableNames = [...usedTableNames, ...this.props.extraUsedTableNames()];
      }
      return usedTableNames;
    };

    tempTableStepUpdateHandler = (fullTable: Table): void => {
      const {table} = this.state.tempFullTableMap[fullTable.name];
      if (fullTable.table !== table) {
        this._setTableOverride(fullTable.name, fullTable.table);
      }
    };

    tempTableStepResetHandler = (name: string): void => {
      const newTable = this.state.fullTableMap[name].table;
      this._setTableOverride(name, newTable);
    };

    _setTableOverride(tableName: string, table: Cells): void {
      const tableUpdateSpec = {$merge: {table}};
      const tempFullTableMap = reactUpdate(this.state.tempFullTableMap, {[tableName]: tableUpdateSpec});
      const selectors = this.props.selectorsFromProps(this.props);
      this.setState({tempFullTableMap});
      // Table overrides are only used by protocols (which only have plain tables) don't want to use
      // a persisted noteRuntimeSnapshot anyhow.
      this._updateEvaluatedTableState(tempFullTableMap, selectors, null);
    }

    _getComputedTables = (): OutputTableMap => this.state.computedTables;

    override render(): JSX.Element {
      return (
        <TableOpContextProvider
          fileId={this.props.fileId ?? null}
          spreadsheetTypes={this.props.spreadsheetTypes!}
          getComputedTables={this._getComputedTables}
          getItems={this.props.getItems!}
          getRoot={this.props.getRoot!}
          getType={this.props.getType!}
          getCid={this.props.getCid!}
          getContent={this.props.getContent as (item: T) => {table: Table}}
          setContent={this.props.setContent!}
          requestSelection={this.props.requestSelection!}
          updateRoot={this.props.updateRoot!}
          itemsAccessor={this.props.itemsAccessor!}
        >
          <WrappedComponent
            {...this.props}
            {...this.state}
            ref={this.props.setRef}
            evaluateTables={this.evaluateTables}
            setMultiEditingForTable={this.setMultiEditingForTable}
            tryInsert={this.tryInsert}
            canInsert={this.canInsert}
            disallowMultiExpand={this.disallowMultiExpand}
            performTableOp={this._performTableOp}
            updateSelectors={this.updateSelectors}
            usedTableNames={this.usedTableNames}
            isTableNameUsed={this.isTableNameUsed}
            tempTableStepUpdateHandler={this.tempTableStepUpdateHandler}
            tempTableStepResetHandler={this.tempTableStepResetHandler}
          />
        </TableOpContextProvider>
      );
    }
  }

  // As we're wrapping the component, we need to move static fields
  // from the component we're wrapping into the provided component.
  // This is needed for things like SimpleTableEditable.standaloneProps.
  hoistNonReactStatics(Component, WrappedComponent);
  return Component;
}

/**
 * Assembles the data needed to do table evaluation while determining whether or not evaluation needs to
 * be performed.
 * @param props the current component props
 * @param state the current state
 * @param nextProps the next component props
 * @param getNoteRuntimeSnapshot method to retrieve the NoteRuntimeSnapshot from props if enabled
 * @returns data needed for evaluation. If shortCircuit is false, fullTableMap will be non-null.
 */
function getDataForUpdatingEvaluatedTableState(
  props: ComponentProps<unknown>,
  state: State,
  nextProps: ComponentProps<unknown>,
  getNoteRuntimeSnapshot: (props: ComponentProps<unknown>) => NoteRuntimeSnapshot | null
): {
  noteRuntimeSnapshot: NoteRuntimeSnapshot | null;
  selectors: Array<Selector>;
  fullTableMap: FullTableMap | null;
  shortCircuit: boolean;
} {
  const result = {
    noteRuntimeSnapshot: getNoteRuntimeSnapshot(nextProps),
    selectors: props.selectorsFromProps(nextProps),
    fullTableMap: null,
    shortCircuit: false,
  };

  // Test 1: If NoteRuntimeSnapshot is not null (persist eval is enabled for this doc type),
  // we can short circuit if current and next NRSs are referentially equal.
  if (result.noteRuntimeSnapshot != null && result.noteRuntimeSnapshot === getNoteRuntimeSnapshot(props)) {
    return {...result, shortCircuit: true};
  }

  let nextFullTableMap: FullTableMap;
  if (result.noteRuntimeSnapshot == null) {
    // Test 2: If NRS is null (persist eval not enabled), we can short circuit if
    // current and next spreadsheet roots and selectors are referentially equal.
    if (
      props.spreadsheetRoot(nextProps) === props.spreadsheetRoot(props) &&
      result.selectors === props.selectorsFromProps(props)
    ) {
      return {...result, shortCircuit: true};
    }

    // Test 3: If NRS is null (persist eval not enabled), we can avoid full evaluation if
    // we generate a new fullTableMap and it's deep-equal to the current fullTableMap in state.
    // Checking table deep quality is somewhat expensive as entries get large, which is why we try to
    // short circuit with quicker checks above.
    nextFullTableMap = props.makeFullTableMap(nextProps);
    if (
      result.selectors === props.selectorsFromProps(props) &&
      isEqual(state.fullTableMap, nextFullTableMap)
    ) {
      return {...result, shortCircuit: true};
    }
  } else {
    nextFullTableMap = props.makeFullTableMap(nextProps);
  }

  return {...result, fullTableMap: nextFullTableMap};
}

export const _forTesting = {
  getDataForUpdatingEvaluatedTableState,
};
